// A one-tap score: five steps, each a number over its word. The chosen step is filled, and its
// state is announced, so the verdict never rests on the fill alone.
import "./config";
import { Text, styled } from "tamagui";
import { pressed } from "./aria";
import { Pressable } from "./controls";
import { Row } from "./layout";

const Step = styled(Pressable, {
  name: "ScoreStep",
  flex: 1,
  gap: 2,
  minHeight: { default: 64, a11yLight: 72, a11yDark: 72 },
  paddingHorizontal: "1",
  paddingVertical: "2",
  borderRadius: "2",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: "surface",
  variants: {
    active: {
      true: { backgroundColor: "accent", borderColor: "accent" },
    },
  } as const,
});

const Figure = styled(Text, {
  name: "ScoreFigure",
  fontFamily: "heading",
  fontSize: "6",
  fontWeight: "700",
  color: "text",
  variants: { active: { true: { color: "accentInk" } } } as const,
});

const Word = styled(Text, {
  name: "ScoreWord",
  fontFamily: "body",
  fontSize: "1",
  fontWeight: "700",
  color: "textDim",
  textAlign: "center",
  variants: { active: { true: { color: "accentInk" } } } as const,
});

export interface ScoreScaleProps<T extends string> {
  /** The group's accessible name. */
  label: string;
  steps: readonly T[];
  value: T | undefined;
  labels: Readonly<Record<T, string>>;
  onChange: (value: T) => void;
  testIdPrefix?: string | undefined;
}

export function ScoreScale<T extends string>({
  label,
  steps,
  value,
  labels,
  onChange,
  testIdPrefix,
}: ScoreScaleProps<T>): React.JSX.Element {
  return (
    <Row role="group" aria-label={label} gap="1">
      {steps.map((step, index) => {
        const active = step === value;
        return (
          <Step
            key={step}
            active={active}
            aria-label={labels[step]}
            // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
            {...pressed(active)}
            // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
            {...(testIdPrefix === undefined ? {} : { "data-testid": `${testIdPrefix}-${step}` })}
            onPress={() => {
              onChange(step);
            }}
          >
            <Figure active={active} aria-hidden>
              {index + 1}
            </Figure>
            <Word active={active}>{labels[step]}</Word>
          </Step>
        );
      })}
    </Row>
  );
}
